// Enhanced Financial Commitments Calculator

// Enhanced Form Submission Manager for Payroll Projections
class PayrollProjectionsFormSubmissionManager extends FormSubmissionManager {
    constructor() {
        super('payrollProjectionsForm');
        this.initAntiSpam();
    }

    initAntiSpam() {
        // Set form load time for anti-spam checking
        const formLoadTimeField = document.getElementById('form_load_time');
        if (formLoadTimeField) {
            formLoadTimeField.value = new Date().toISOString();
        }
    }

    async handleSubmit(event) {
        event.preventDefault();
        
        // Prevent double submission
        if (this.isSubmitting) return;
        
        // Validate form before showing loading
        if (!this.validatePayrollForm()) {
            this.showWarning('Please fill in all required fields before submitting.');
            return;
        }

        // Show loading with initial message
        this.showLoading('Processing Payroll Projections', 'Calculating your financial commitments...');
        
        try {
            // Step 1: Collect and validate data
            this.updateProgress(20, 'Collecting form data...');
            const formData = this.collectFormData();
            
            // Step 2: Prepare webhook data
            this.updateProgress(40, 'Preparing submission data...');
            const webhookData = this.prepareWebhookData(formData);
            
            // Step 3: Submit to webhook proxy (which also saves to database and generates PDF)
            this.updateProgress(60, 'Submitting your data...');
            const webhookResult = await this.makeRequest('/Forms/PayrollProjectionsWebhookProxy', webhookData);
            
            if (!webhookResult.success) {
                throw new Error(webhookResult.error || 'Failed to submit payroll projection');
            }
            
            this.updateProgress(90, 'Submission complete!');
            
            // Step 4: Success
            this.updateProgress(100, 'Redirecting...');
            
            // Reset form
            this.form.reset();
            calculateTotalCommitment();
            
            // Show success and redirect
            this.showSuccess('Your Payroll Projections have been submitted successfully!');
            
        } catch (error) {
            console.error('Payroll Projections submission failed:', error);
            
            // Determine error type and show appropriate message
            if (error.message.includes('database') || error.message.includes('save')) {
                this.showError(
                    'There was a problem saving your submission to our database. Please try again or contact support if the problem persists.',
                    true
                );
            } else if (error.message.includes('network') || error.message.includes('fetch')) {
                this.showError(
                    'There was a network connection problem. Please check your internet connection and try again.',
                    true
                );
            } else {
                this.showError(
                    'An unexpected error occurred while processing your submission. Please try again or contact support if the problem persists.',
                    true
                );
            }
        }
    }

    validatePayrollForm() {
        const requiredFields = document.querySelectorAll('input[required], select[required]');
        let isValid = true;
        let firstInvalidField = null;

        requiredFields.forEach(field => {
            if (!field.value.trim()) {
                field.style.borderColor = '#dc2626';
                field.style.backgroundColor = '#fef2f2';
                if (!firstInvalidField) firstInvalidField = field;
                isValid = false;
            } else {
                field.style.borderColor = '#d1d5db';
                field.style.backgroundColor = '#ffffff';
            }
        });

        if (!isValid && firstInvalidField) {
            firstInvalidField.focus();
            firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return isValid;
    }

    collectFormData() {
        const firstNameElement = document.getElementById('firstName');
        const middleNameElement = document.getElementById('middleName');
        const lastNameElement = document.getElementById('lastName');
        const businessNameElement = document.getElementById('businessName');
        const annualSalaryElement = document.getElementById('annualSalary');
        const hasPartnerElement = document.getElementById('hasPartner');
        const partnerIncomeElement = document.getElementById('partnerIncome');
        const totalCommitmentElement = document.getElementById('totalCommitment');
        const businessIdElement = document.getElementById('businessId');
        const emailElement = document.getElementById('email');
        const phoneElement = document.getElementById('phone');
        const currentSalaryElement = document.getElementById('currentSalary');
        
        const firstName = firstNameElement ? firstNameElement.value : '';
        const middleName = middleNameElement ? middleNameElement.value : '';
        const lastName = lastNameElement ? lastNameElement.value : '';
        const fullName = [firstName, middleName, lastName].filter(name => name.trim()).join(' ');
        
        return {
            applicantName: fullName,
            firstName: firstName,
            middleName: middleName,
            lastName: lastName,
            businessId: businessIdElement ? businessIdElement.value : '',
            email: emailElement ? emailElement.value : '',
            phone: phoneElement ? phoneElement.value : '',
            currentSalary: currentSalaryElement ? parseFloat(currentSalaryElement.value) || 0 : 0,
            businessName: businessNameElement ? businessNameElement.value : '',
            annualSalary: annualSalaryElement ? parseFloat(annualSalaryElement.value) || 0 : 0,
            hasPartner: hasPartnerElement ? hasPartnerElement.value === 'yes' : false,
            partnerIncome: partnerIncomeElement ? parseFloat(partnerIncomeElement.value) || 0 : 0,
            totalWeeklyCommitment: totalCommitmentElement ? parseFloat(totalCommitmentElement.textContent.replace('$', '').replace(',', '')) || 0 : 0,
            commitmentsData: JSON.stringify(getAllCommitments()),
            notes: `Form submitted on ${formatAustralianDate(new Date().toISOString().split('T')[0])}`
        };
    }

    prepareWebhookData(formData) {
        const allCommitments = getAllCommitments();
        
        return {
            form_type: 'Payroll Projections',
            first_name: formData.firstName,
            middle_name: formData.middleName,
            last_name: formData.lastName,
            business_id: formData.businessId,
            full_name: formData.applicantName,
            email: formData.email,
            phone: formData.phone,
            current_salary: formData.currentSalary,
            business_name: formData.businessName,
            annual_salary: formData.annualSalary,
            has_partner: formData.hasPartner ? 'yes' : 'no',
            partner_income: formData.partnerIncome,
            total_weekly_commitment: formData.totalWeeklyCommitment,
            
            // Detailed commitments data
            commitments: allCommitments,
            commitments_count: allCommitments.length,
            
            // Commitments summary by type
            commitments_by_type: groupCommitmentsByType(allCommitments),
            
            // Individual commitment details for easy access
            education_costs: getCommitmentsByType(allCommitments, 'Education Costs'),
            entertainment: getCommitmentsByType(allCommitments, 'Entertainment'),
            family_recreational: getCommitmentsByType(allCommitments, 'Family Recreational'),
            government_fee: getCommitmentsByType(allCommitments, 'Government Fee'),
            groceries: getCommitmentsByType(allCommitments, 'Groceries'),
            health_care_insurance: getCommitmentsByType(allCommitments, 'Health Care & Insurance'),
            household_care: getCommitmentsByType(allCommitments, 'Household Care'),
            investment_repayment: getCommitmentsByType(allCommitments, 'Investment Repayment'),
            landlord_insurance: getCommitmentsByType(allCommitments, 'Landlord Insurance Premiums'),
            lifestyle_leisure: getCommitmentsByType(allCommitments, 'Lifestyle & Leisure'),
            mortgage_repayment: getCommitmentsByType(allCommitments, 'Mortgage Repayment'),
            motor_vehicle_loan: getCommitmentsByType(allCommitments, 'Motor Vehicle Loan Repayment'),
            motor_vehicle_expenses: getCommitmentsByType(allCommitments, 'Motor Vehicles Expenses'),
            personal_family_care: getCommitmentsByType(allCommitments, 'Personal & Family Care'),
            private_health_cover: getCommitmentsByType(allCommitments, 'Private Health Cover'),
            rent_payment: getCommitmentsByType(allCommitments, 'Rent Payment'),
            travel_transport: getCommitmentsByType(allCommitments, 'Travel & Transport'),
            utilities_bills: getCommitmentsByType(allCommitments, 'Utilities Bills'),
            
            // Summary totals
            total_monthly_commitments: calculateTotalByFrequency(allCommitments, 'monthly'),
            total_fortnightly_commitments: calculateTotalByFrequency(allCommitments, 'fortnightly'),
            total_weekly_commitments: calculateTotalByFrequency(allCommitments, 'weekly'),
            grand_total_weekly_equivalent: allCommitments.reduce((sum, commitment) => sum + commitment.weekly_amount, 0),
            
            submission_date: new Date().toISOString(),
            timestamp: Date.now()
        };
    }
}

// Make functions globally available
window.toggleOtherIncomeFields = function() {
    const hasOtherIncome = document.getElementById('hasOtherIncome').value;
    const otherIncomeFields = document.getElementById('otherIncomeFields');
    
    if (hasOtherIncome === 'yes') {
        otherIncomeFields.classList.remove('hidden');
        // Make fields required when visible
        document.getElementById('businessName').required = true;
        document.getElementById('annualSalary').required = true;
    } else {
        otherIncomeFields.classList.add('hidden');
        // Clear and make fields not required
        document.getElementById('businessName').value = '';
        document.getElementById('annualSalary').value = '';
        document.getElementById('businessName').required = false;
        document.getElementById('annualSalary').required = false;
    }
}

window.togglePartnerFields = function() {
    const hasPartner = document.getElementById('hasPartner').value;
    const partnerFields = document.getElementById('partnerFields');
    
    if (hasPartner === 'yes') {
        partnerFields.classList.remove('hidden');
    } else {
        partnerFields.classList.add('hidden');
        // Reset partner working and income fields
        document.getElementById('partnerWorking').value = '';
        document.getElementById('partnerIncome').value = '';
        document.getElementById('partnerIncomeField').classList.add('hidden');
    }
}

window.togglePartnerIncomeField = function() {
    const partnerWorking = document.getElementById('partnerWorking').value;
    const partnerIncomeField = document.getElementById('partnerIncomeField');
    
    if (partnerWorking === 'yes') {
        partnerIncomeField.classList.remove('hidden');
        document.getElementById('partnerIncome').required = true;
    } else {
        partnerIncomeField.classList.add('hidden');
        document.getElementById('partnerIncome').value = '';
        document.getElementById('partnerIncome').required = false;
    }
}

// Enhanced calculation function with better conversion rates
window.calculateWeeklyAmount = function(amountInput) {
    try {
        const row = amountInput.closest('tr');
        const frequencySelect = row.querySelector('.commitment-frequency');
        const weeklyAmountSpan = row.querySelector('.weekly-amount');
        
        if (!frequencySelect || !weeklyAmountSpan) {
            console.warn('Missing frequency select or weekly amount span');
            return;
        }
        
        const amount = parseFloat(amountInput.value) || 0;
        const frequency = frequencySelect.value;
        
        let weeklyAmount = 0;
        
        // Convert to weekly amounts using precise conversion rates
        switch (frequency) {
            case 'weekly':
                weeklyAmount = amount;
                break;
            case 'fortnightly':
                weeklyAmount = amount / 2;
                break;
            case 'monthly':
                // More precise monthly to weekly conversion (52 weeks / 12 months = 4.333...)
                weeklyAmount = amount * 12 / 52;
                break;
            default:
                weeklyAmount = amount; // Default to weekly if frequency not recognized
        }
        
        // Update the weekly amount display with proper formatting
        weeklyAmountSpan.textContent = weeklyAmount.toFixed(2);
        
        // Add visual feedback for non-zero amounts
        if (weeklyAmount > 0) {
            weeklyAmountSpan.style.fontWeight = 'bold';
            weeklyAmountSpan.style.color = '#B8941F'; // Green color for positive amounts
        } else {
            weeklyAmountSpan.style.fontWeight = 'normal';
            weeklyAmountSpan.style.color = '#6b7280'; // Gray color for zero amounts
        }
        
        // Recalculate total
        calculateTotalCommitment();
        
    } catch (error) {
        console.error('Error calculating weekly amount:', error);
    }
}

// Enhanced total calculation with validation and formatting
function calculateTotalCommitment() {
    try {
        const weeklyAmounts = document.querySelectorAll('.weekly-amount');
        let total = 0;
        let itemCount = 0;
        
        weeklyAmounts.forEach(span => {
            const value = parseFloat(span.textContent) || 0;
            total += value;
            if (value > 0) itemCount++;
        });
        
        // Update total display with formatting
        const totalElement = document.getElementById('totalCommitment');
        if (totalElement) {
            totalElement.textContent = total.toFixed(2);

            // Update the parent container to show item count
            const parentContainer = totalElement.closest('.bg-yellow-50');
            if (parentContainer) {
                let summaryText = parentContainer.querySelector('.commitment-summary');
                if (!summaryText) {
                    summaryText = document.createElement('div');
                    summaryText.className = 'commitment-summary text-sm text-gray-600 mt-2';
                    parentContainer.appendChild(summaryText);
                }
                summaryText.textContent = `${itemCount} active commitment${itemCount !== 1 ? 's' : ''} totaling $${total.toFixed(2)} per week`;
            }
        }
        
    } catch (error) {
        console.error('Error calculating total commitment:', error);
    }
}

// Helper function to format dates in DD/MM/YYYY format
function formatAustralianDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
}

// Add input event listeners for real-time calculation
function setupRealTimeCalculations() {
    const amountInputs = document.querySelectorAll('.commitment-amount');
    const frequencySelects = document.querySelectorAll('.commitment-frequency');
    
    // Add input event listeners for real-time updates
    amountInputs.forEach(input => {
        input.addEventListener('input', function() {
            calculateWeeklyAmount(this);
        });
        
        input.addEventListener('blur', function() {
            // Format the input value on blur
            if (this.value && !isNaN(this.value)) {
                this.value = parseFloat(this.value).toFixed(2);
            }
        });
    });
    
    // Add change listeners to frequency selects
    frequencySelects.forEach(select => {
        select.addEventListener('change', function() {
            const row = this.closest('tr');
            const amountInput = row.querySelector('.commitment-amount');
            if (amountInput) {
                calculateWeeklyAmount(amountInput);
            }
        });
    });
}

// Utility function to export calculation summary
function getCalculationSummary() {
    const summary = {};
    const rows = document.querySelectorAll('#commitmentsTable tr');
    
    rows.forEach(row => {
        const categoryElement = row.querySelector('strong');
        const amountInput = row.querySelector('.commitment-amount');
        const frequencySelect = row.querySelector('.commitment-frequency');
        const weeklyAmount = row.querySelector('.weekly-amount');
        
        if (categoryElement && amountInput && frequencySelect && weeklyAmount) {
            const category = categoryElement.textContent;
            const amount = parseFloat(amountInput.value) || 0;
            const frequency = frequencySelect.value;
            const weekly = parseFloat(weeklyAmount.textContent) || 0;
            
            if (amount > 0) {
                summary[category] = {
                    originalAmount: amount,
                    frequency: frequency,
                    weeklyAmount: weekly
                };
            }
        }
    });
    
    summary.totalWeekly = parseFloat(document.getElementById('totalCommitment').textContent) || 0;
    return summary;
}

function getAllCommitments() {
    const commitments = [];
    const rows = document.querySelectorAll('#commitmentsTable tr');
    
    rows.forEach(row => {
        const cells = row.querySelectorAll('td');
        if (cells.length >= 4) {
            // Extract category name from the first cell (remove description text)
            const categoryElement = cells[0].querySelector('strong');
            const category = categoryElement ? categoryElement.textContent.trim() : '';
            
            // Extract amount from input field
            const amountInput = cells[1].querySelector('input[type="number"]');
            const amount = amountInput ? parseFloat(amountInput.value) || 0 : 0;
            
            // Extract frequency from select field
            const frequencySelect = cells[2].querySelector('select');
            const frequency = frequencySelect ? frequencySelect.value : '';
            
            // Extract weekly amount from span
            const weeklyAmountSpan = cells[3].querySelector('.weekly-amount');
            const weeklyAmount = weeklyAmountSpan ? parseFloat(weeklyAmountSpan.textContent) || 0 : 0;
            
            // Only include commitments with actual amounts
            if (category && amount > 0) {
                commitments.push({
                    category: category,
                    amount: amount,
                    frequency: frequency,
                    weekly_amount: weeklyAmount,
                    formatted_amount: `$${amount.toFixed(2)}`,
                    formatted_weekly_amount: `$${weeklyAmount.toFixed(2)}`
                });
            }
        }
    });
    
    return commitments;
}

// Helper function to group commitments by type with totals
function groupCommitmentsByType(commitments) {
    const grouped = {};
    
    commitments.forEach(commitment => {
        const category = commitment.category;
        if (!grouped[category]) {
            grouped[category] = {
                items: [],
                total_amount: 0,
                total_weekly_amount: 0,
                count: 0
            };
        }
        
        grouped[category].items.push(commitment);
        grouped[category].total_amount += commitment.amount;
        grouped[category].total_weekly_amount += commitment.weekly_amount;
        grouped[category].count++;
    });
    
    return grouped;
}

// Helper function to get commitments by specific type
function getCommitmentsByType(commitments, category) {
    return commitments.filter(commitment => commitment.category === category);
}

// Helper function to calculate total by frequency
function calculateTotalByFrequency(commitments, frequency) {
    return commitments
        .filter(commitment => commitment.frequency === frequency)
        .reduce((sum, commitment) => sum + commitment.amount, 0);
}

// Initialize form with enhanced submission handling
let formSubmissionManager;

document.addEventListener('DOMContentLoaded', function() {
    console.log('Initializing Payroll Projections calculator...');
    
    // Initialize enhanced form submission manager
    formSubmissionManager = new PayrollProjectionsFormSubmissionManager();
    
    // Setup real-time calculations
    setupRealTimeCalculations();
    
    // Initial calculation for all fields
    const amountInputs = document.querySelectorAll('.commitment-amount');
    amountInputs.forEach(input => {
        calculateWeeklyAmount(input);
    });
    
    console.log('Payroll Projections calculator initialized successfully with enhanced submission handling');
});

// Clean up on page unload
window.addEventListener('beforeunload', function() {
    if (formSubmissionManager) {
        formSubmissionManager.destroy();
    }
});
